// Import side of the layout bridge: seeds the panels slice and the per-type panel
// slices for a dashboard from a LayoutConfigV1 the server already validated.
//
// No client-side validation runs here — pi-server is the validator of record
// and every config the client ingests came back from a server
// response. The panels slice still revalidates module *types* (unregistered
// types are dropped) via its setPreferences reducer.
//
// Placement is NOT applied here: `config.arrangement` is the verbatim Dockview
// blob; the caller feeds it to the layout-engine persistence load path for the
// same instance. Wiring this into the instance-open flow is APP-2705's job.
import type { UnknownAction } from "@reduxjs/toolkit"
import type { AppDispatch } from "../../../../store"
import { getPanelIdForModule } from "../helpers/module-renderers"
import { assetViewSlice } from "../store/asset-view-slice"
import { factorsViewSlice } from "../store/factors-view-slice"
import { graphSlice } from "../store/graph-slice"
import { kpiSlice } from "../store/kpi-slice"
import { panelsSlice } from "../store/panels-slice"
import type { DashboardModule } from "../modules/types"
import { portableGridToDatagridState, type PortableGrid } from "./portable-grid"
import type { LayoutConfigV1, Panel } from "../contracts/layouts"

function buildModuleForPanel(panel: Panel): DashboardModule {
  return {
    id: panel.key,
    type: panel.moduleType,
    config: panel.title !== undefined ? { title: panel.title } : {},
  }
}

// Reassemble the opaque datagridState the slice stores from the contract's grid
// fields. Returns undefined when the layout carried no arrangement, so seeding
// leaves the slice's fresh-instance default ({}) untouched.
function seedDatagridState(grid: PortableGrid): Record<string, unknown> | undefined {
  if (!grid.orderedFields && !grid.pinnedColumns && !grid.sortModel) return undefined
  return portableGridToDatagridState(grid)
}

// Returns undefined for a moduleType with no seedable slice — TS treats the
// switch as exhaustive over Panel["moduleType"], but the trust-server cast
// means a deploy-skew config can carry a moduleType this client
// build doesn't know; the caller skips those rather than dispatching undefined.
function buildSeedAction(dashboardId: string, panelId: string, panel: Panel): UnknownAction | undefined {
  switch (panel.moduleType) {
    case "asset-view": {
      const { view, groupBy, selectedTableLevels, showNotHeld, columnOrder, orderedFields, pinnedColumns, sortModel } = panel.viewConfig
      const datagridState = seedDatagridState({ orderedFields, pinnedColumns, sortModel })
      return assetViewSlice.actions.setPreferences({
        dashboardId,
        panelId,
        ...(view !== undefined && { view }),
        ...(groupBy !== undefined && { groupBy }),
        ...(selectedTableLevels !== undefined && { selectedTableLevels }),
        ...(showNotHeld !== undefined && { showNotHeld }),
        ...(columnOrder !== undefined && { columnOrder }),
        ...(datagridState !== undefined && { datagridState }),
      })
    }
    case "kpi": {
      const { sectionSettings, selectedGroup, selectedTab } = panel.viewConfig
      return kpiSlice.actions.setPreferences({
        dashboardId,
        panelId,
        ...(sectionSettings !== undefined && { sectionSettings }),
        ...(selectedGroup !== undefined && { selectedGroup }),
        ...(selectedTab !== undefined && { selectedTab }),
      })
    }
    case "graph": {
      const { selectedTemplate, disabledItems } = panel.viewConfig
      return graphSlice.actions.setPreferences({
        dashboardId,
        panelId,
        ...(selectedTemplate !== undefined && { selectedTemplate }),
        ...(disabledItems !== undefined && { disabledItems }),
      })
    }
    case "factors": {
      const { view, groupBy, showNotHeld, selectedPeriodPerformance, sortMetricId, sortDirection, selectedTableLevels, orderedFields, pinnedColumns } = panel.viewConfig
      // Factors carries no sortModel — sort travels via sortMetricId/sortDirection.
      const datagridState = seedDatagridState({ orderedFields, pinnedColumns })
      return factorsViewSlice.actions.setPreferences({
        dashboardId,
        panelId,
        ...(view !== undefined && { view }),
        // The contract carries the grouping dimension id; the slice stores the
        // taxonomy object — the label is re-resolved at render time.
        ...(groupBy !== undefined && { groupBy: groupBy === null ? null : { id: groupBy } }),
        ...(showNotHeld !== undefined && { showNotHeld }),
        ...(selectedPeriodPerformance !== undefined && { selectedPeriodPerformance }),
        ...(sortMetricId !== undefined && { sortMetricId }),
        ...(sortDirection !== undefined && { sortDirection }),
        ...(selectedTableLevels !== undefined && { selectedTableLevels }),
        ...(datagridState !== undefined && { datagridState }),
      })
    }
    default:
      return undefined
  }
}

/**
 * Seed the dashboard slices for `dashboardId` from a LayoutConfigV1: the panels
 * slice gets the modules list (via setPreferences → validateModules), then each
 * panel's viewConfig seeds its per-type slice via setPreferences.
 *
 * Authority: setPreferences marks each slot preferences-loaded, so on mount the
 * sync hooks (`wasInitializedAtMount` → `skipInitialLoad`) treat the seed as the
 * loaded preferences and do NOT fetch the user's saved per-panel prefs — the
 * imported layout wins. That is correct for the import flow but is the opposite
 * of "seed is a baseline that saved state overrides"; if a future caller needs
 * baseline semantics it must seed through a guarded `initInstance`-style reducer
 * that leaves `isPreferencesLoaded` untouched. No caller wires this yet (APP-2705).
 *
 * Precondition: `dashboardId` is a fresh instance (the import/lazy-migration flow
 * creates a new instance). Seeding only adds/overwrites per-type slots for the
 * config's panels — it does not clear slots from a prior config, so importing onto
 * an already-populated dashboard would orphan the previous panels' per-type slice
 * state (`cleanupPanelSlices` is not called here).
 *
 * Returns the *requested* module list (built from `config.panels`, pre-validation)
 * so the caller can pair it with `config.arrangement` for the layout-engine load
 * path. This is what the config asked for, NOT necessarily what the panels slice
 * holds: `setPreferences` runs `validateModules`, which drops unregistered /
 * deploy-skew types. A caller needing the *applied* set must read it back from the
 * panels slice.
 */
export function initFromLayoutConfig(
  dispatch: AppDispatch,
  dashboardId: string,
  config: LayoutConfigV1,
): DashboardModule[] {
  const { panels } = config
  const modules = panels.map(buildModuleForPanel)
  dispatch(panelsSlice.actions.setPreferences({ dashboardId, modules }))
  // Derive each panel id from the panel being seeded (panel.key/moduleType), not
  // from a parallel modules[index] — the panel id is a pure function of the panel,
  // so there is no index-alignment assumption to break. Skip panels with no
  // seedable slice: a deploy-skew moduleType this client build doesn't know
  // (validateModules already dropped it from the panels slice), so seeding it
  // would both dispatch undefined and orphan per-type slice state.
  panels.forEach((panel) => {
    const seedAction = buildSeedAction(dashboardId, getPanelIdForModule(buildModuleForPanel(panel)), panel)
    if (seedAction) dispatch(seedAction)
  })
  return modules
}
